/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import * as React from 'react';
import './AllCurvesTable.scss';
import { Table, TableHead, TableRow, TableCell, TableBody, TableSortLabel, TableCellType, SortDirection } from '@vaisala-rockhopper/components';
import { useTranslation } from 'react-i18next';
import { CurveData } from '../../../../../modules/ChemicalDataController';

interface AllCurvesTableProps {
  curves: Array<CurveData>
  onChemicalChanged(id: number): void
}

const AllCurvesTable = ({curves, onChemicalChanged}: AllCurvesTableProps) => {
  const { t } = useTranslation();

  const [order, setOrder] = React.useState<SortDirection | null>(SortDirection.ASC);
  const [orderBy, setOrderBy] = React.useState<string | null>('Name');

  function descendingComparator(a: any, b: any, orderBy: any) {
    if (typeof a[orderBy] === "string" && typeof b[orderBy] === "string") {
      return b[orderBy].localeCompare(a[orderBy]);
    } else if (typeof a[orderBy] === "number" && typeof b[orderBy] === "number") {
      return a[orderBy] - b[orderBy];
    } else if (typeof a[orderBy] === "string") {
      return -1;
    } else {
      return 1;
    }
  }


  function getComparator(order: any, orderBy: any) {
    return order === 'DESC'
      ? (a: any, b: any) => descendingComparator(a, b, orderBy)
      : (a: any, b: any) => -descendingComparator(a, b, orderBy)
  }

  function stableSort(array: any, comparator: any, orderBy: any) {
    const stabilizedThis = array.map((el: any, index: any) => [el, index]);
    stabilizedThis.sort((a: any, b: any) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el: any) => el[0]);
  }

  const headerCells = [
    {id: "Name", type: undefined, sortable: true, label: t('table.name')},
    {id: "Code", type: TableCellType.Numeric, sortable: true, label: t('table.code')},
    {id: "ConcMin", type: TableCellType.Numeric, sortable: true, label: t('table.min')},
    {id: "ConcMax", type: TableCellType.Numeric, sortable: true, label: t('table.max')},
    {id: "Unit", type: undefined, sortable: false, label: t('table.unit')},
    {id: "Date", type: TableCellType.Numeric, sortable: false, label: t('table.modified')}
  ];

  const createSortClickHandler = (cellId: string, onSort: Function) => (event: any) => {
    onSort(event, cellId);
  };

  function TableHeader(props: any) {
    const {orderBy, order, onSort} = props;

    return(
      <TableHead>
        <TableRow>
          {headerCells.map((cell, index) => {
            return(
              <TableCell isSortable={true} key={`cell_${cell.id}_${index}`} type={cell.type}>
                <TableSortLabel
                  key={`sort_label_${cell.id}_${index}`}
                  active={orderBy === cell.id}
                  direction={orderBy === cell.id ? order : SortDirection.ASC}
                  onClick={createSortClickHandler(cell.id, onSort)}
                >
                  {cell.label}
                </TableSortLabel>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  const handleSort = (event: any, property: any) => {
    const isAsc = orderBy === property && order === 'ASC';
    const isDesc = orderBy === property && order === 'DESC';
    if(isAsc) {
        setOrder(SortDirection.DESC);
        setOrderBy(property);
    } else if (isDesc) {
        setOrder(SortDirection.ASC);
        setOrderBy('Name');
    } else {
        setOrder(SortDirection.ASC);
        setOrderBy(property);
    }
  };

  const tableClicked = (e: any) => {
    if (e.target.closest('tr') && e.target.tagName === "TD") {
      const id = parseInt(e.target.closest('tr').getAttribute('data-id'));
      if (id !== undefined) {
        onChemicalChanged(id)
      }
    }
  }

  return (
    <div className="all-curves-table">
      <Table fixedHeader={true}  onClick={(e) => tableClicked(e)}>
        <TableHeader
          order={order}
          orderBy={orderBy}
          onSort={handleSort}
        ></TableHeader>
       <TableBody>
       {stableSort(curves, getComparator(order, orderBy), orderBy).map((val: CurveData, i: number) => {
            return (
              <TableRow key={`row_${i}`} data-id={val.Id}>
                <TableCell>
                  {val.Confidential && <span className="confidential-circle"></span> }
                  {val.Name}
                </TableCell>
                <TableCell type={TableCellType.Numeric}>
                  {val.Code}
                </TableCell>
                <TableCell type={TableCellType.Numeric}>
                  {val.ConcMin}
                </TableCell>
                <TableCell type={TableCellType.Numeric}>
                  {val.ConcMax}
                </TableCell>
                <TableCell>
                  {val.Unit}
                </TableCell>
                <TableCell>
                  {val.Date}
                </TableCell>
              </TableRow>
            );
          })}
       </TableBody>
     </Table>
    </div>
  );
};
export default AllCurvesTable;
